import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap";
import { nanoid } from "nanoid";
import { useFirebase } from "model/context/firebase.context";
import { useAuth } from "model/context/auth.context";
import { COLLECTIONS } from "model/constants";

export function ProjectTicketModal({
  projectId,
  openModal,
  setModalOpen,
  setError,
  editTicket,
}) {
  const { session } = useAuth();
  const { db } = useFirebase();

  const [loading, setLoading] = useState(false);
  const [allProjects, setAllProjects] = useState([]);
  const [taskText, setTaskText] = useState("");
  const [commentText, setCommentText] = useState("");

  const [ticketData, setTicketData] = useState({
    projectName: "",
    name: "",
    description: "",
    instructions: "",
    instructionsFor: "",
    priority: "Low",
    tasks: [],
  });

  const [commentsList, setCommentsList] = useState([]);

  useEffect(() => {
    if (editTicket) {
      setTicketData((prev) => ({
        ...prev,
        name: editTicket.name,
        description: editTicket.description,
        instructions: editTicket.instructions || "",
        instructionsFor: editTicket.instructionsFor || "",
        priority: editTicket.priority,
        tasks: editTicket.tasks || [],
      }));
      setCommentsList(editTicket.commentsList || []);
    } else {
      setTicketData({
        projectName: "",
        name: "",
        description: "",
        instructions: "",
        instructionsFor: "",
        priority: "Low",
        tasks: [],
      });
      setCommentsList([]);
    }
  }, [editTicket]);

  useEffect(() => {
    if (projectId) {
      db.get(COLLECTIONS.projects, projectId)
        .then((doc) => {
          if (!doc) return;
          const project = JSON.parse(JSON.stringify(doc));
          setTicketData((prev) => ({
            ...prev,
            projectName: project.name || "",
          }));
        })
        .catch((err) => setError(err.message || "Could not fetch project."));
    } else {
      const unsubscribe = db.listen(COLLECTIONS.projects, (data) => {
        if (!data) {
          setAllProjects([]);
          return;
        }
        const mapped = Object.entries(data).map(([id, value]) => ({
          id,
          ...value,
        }));
        setAllProjects(mapped);
      });
      return () => unsubscribe();
    }
  }, [projectId, db, setError]);

  const addTask = () => {
    const rawTasks = taskText.split(",").map((t) => t.trim()).filter(Boolean);
    if (rawTasks.length === 0) return;
  
    const newTasks = rawTasks.map((text) => ({
      id: nanoid(),
      text,
      done: false,
    }));
  
    setTicketData((prev) => ({
      ...prev,
      tasks: [...prev.tasks, ...newTasks],
    }));
  
    setTaskText("");
  };

  const toggleTask = (id) => {
    setTicketData((prev) => ({
      ...prev,
      tasks: prev.tasks.map((task) =>
        task.id === id ? { ...task, done: !task.done } : task
      ),
    }));
  };

  const deleteTask = (id) => {
    setTicketData((prev) => ({
      ...prev,
      tasks: prev.tasks.filter((task) => task.id !== id),
    }));
  };

  const addComment = () => {
    if (!commentText.trim()) return;
    const newComment = {
      id: nanoid(),
      text: commentText,
      author: session.user.displayName || session.user.email,
      date: new Date().toLocaleString(),
    };
    setCommentsList((prev) => [...prev, newComment]);
    setCommentText("");
  };

  const deleteComment = (id) => {
    setCommentsList((prev) => prev.filter((c) => c.id !== id));
  };

  const onSubmitTicket = async () => {
    try {
      setLoading(true);
      let usedProjectId = projectId;
      if (!usedProjectId) {
        const chosenProject = allProjects.find(
          (p) => p.name === ticketData.projectName
        );
        if (!chosenProject) {
          setError("Please select a valid project.");
          return;
        }
        usedProjectId = chosenProject.id;
      }

      const projectSnap = await db.get(COLLECTIONS.projects, usedProjectId);
      if (!projectSnap) {
        setError("Project not found.");
        return;
      }
      const projectPlain = JSON.parse(JSON.stringify(projectSnap));
      if (!projectPlain.tickets) {
        projectPlain.tickets = {};
      }

      const ticketId = editTicket ? editTicket.id : nanoid(10);

      projectPlain.tickets[ticketId] = {
        ...projectPlain.tickets[ticketId],
        ...ticketData,
        commentsList,
        ...(editTicket
          ? {}
          : {
              createdBy: session.user.displayName || session.user.email,
              createdAt: new Date().toISOString(),
              checked: false,
            }),
      };

      await db.update(COLLECTIONS.projects, usedProjectId, projectPlain);
      setModalOpen(false);
    } catch (err) {
      setError(err.message || "Error saving ticket.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={openModal}
      toggle={() => setModalOpen(false)}
      size="xl"
      style={{ maxWidth: "120%", width: "120%" }}
    >
      <ModalHeader toggle={() => setModalOpen(false)}>
        {editTicket ? "Edit Ticket" : "Add Ticket"}
      </ModalHeader>
      <ModalBody>
        <Row>
          {/* Column 1: Project, Ticket Name, Description, Tasks */}
          <Col xs="12" md="4">
            <FormGroup>
              <Label for="projectName">Project</Label>
              {projectId ? (
                <Input disabled value={ticketData.projectName} />
              ) : (
                <Input
                  type="select"
                  value={ticketData.projectName}
                  onChange={(e) =>
                    setTicketData((prev) => ({
                      ...prev,
                      projectName: e.target.value,
                    }))
                  }
                >
                  <option value="">-- Select a project --</option>
                  {allProjects.map((proj) => (
                    <option key={proj.id} value={proj.name}>
                      {proj.name}
                    </option>
                  ))}
                </Input>
              )}
            </FormGroup>
            <FormGroup>
              <Label for="ticketName">Ticket Name</Label>
              <Input
                type="text"
                required
                value={ticketData.name}
                onChange={(e) =>
                  setTicketData((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </FormGroup>
            {/* <FormGroup>
              <Label for="ticketDescription">Ticket Description</Label>
              <Input
                type="textarea"
                rows={3}
                required
                value={ticketData.description}
                onChange={(e) =>
                  setTicketData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
            </FormGroup> */}
            <FormGroup>
              <Label for="ticketInstructions">Instructions</Label>
              <Input
                type="textarea"
                rows={5}
                value={ticketData.instructions}
                onChange={(e) =>
                  setTicketData((prev) => ({
                    ...prev,
                    instructions: e.target.value,
                  }))
                }
              />
            </FormGroup>
            <FormGroup>
              <Label for="ticketInstructionsFor">Instructions For</Label>
              <Input
                type="text"
                value={ticketData.instructionsFor}
                onChange={(e) =>
                  setTicketData((prev) => ({
                    ...prev,
                    instructionsFor: e.target.value,
                  }))
                }
              />
            </FormGroup>
            <FormGroup>
              <Label for="ticketPriority">Priority</Label>
              <Input
                type="select"
                value={ticketData.priority}
                onChange={(e) =>
                  setTicketData((prev) => ({
                    ...prev,
                    priority: e.target.value,
                  }))
                }
              >
                <option>Low</option>
                <option>Medium</option>
                <option>High</option>
              </Input>
            </FormGroup>
          </Col>


          {/* Column 3: Tasks */}
          <Col xs="12" md="4">
          <FormGroup>
              <Label>Tasks</Label>
              <div className="d-flex mb-2">
                <Input
                  type="text"
                  value={taskText}
                  onChange={(e) => setTaskText(e.target.value)}
                  placeholder="Enter task..."
                />
                <Button color="primary" onClick={addTask} className="ms-2">
                  +
                </Button>
              </div>
              <div
                              style={{
                                maxHeight: "500px",
                                overflowY: "auto",}}>
                {ticketData.tasks.map((task) => (
                  <FormGroup
                    check
                    key={task.id}
                    className="d-flex align-items-center justify-content-between"
                  >
                    <Label check className="flex-grow-1">
                      <Input
                        type="checkbox"
                        checked={task.done}
                        onChange={() => toggleTask(task.id)}
                      />
                      {task.text}
                    </Label>
                    <Button
                      onClick={() => deleteTask(task.id)}
                      style={{
                        border: "none",
                        background: "none",
                        color: "red",
                        fontSize: "1.5rem",
                        fontWeight: "bold",
                        lineHeight: "1",
                        padding: "0 0.5rem",
                      }}
                    >
                      -
                    </Button>
                  </FormGroup>
                ))}
              </div>
            </FormGroup>
            </Col>

          {/* Column 4: Comments */}
          <Col xs="12" md="4">
            <FormGroup>
              <Label for="ticketComments">Ticket Comments</Label>
              <div className="d-flex mb-2">
              <Input
  type="textarea"
  rows={3}
  value={commentText}
  onChange={(e) => setCommentText(e.target.value)}
  placeholder="Add a comment..."
  style={{ whiteSpace: "pre-wrap" }}
/>

                <Button color="primary" onClick={addComment} className="ms-2">
                  +
                </Button>
              </div>
              <div
                style={{
                  maxHeight: "450px",
                  overflowY: "auto",
                  border: "1px solid #ccc",
                  padding: "0.5rem",
                  borderRadius: "5px",
                  backgroundColor: "#f9f9f9",
                }}
              >
                {commentsList.map((comment) => (
                  <div
                    key={comment.id}
                    className="d-flex justify-content-between align-items-start mb-2"
                    style={{whiteSpace: "break-spaces", borderBottom: "1px solid"}}
                  >
                    <div>
                      <strong>{comment.author}</strong>{" "}
                      <small>({comment.date})</small>
                      <div>{comment.text}</div>
                    </div>
                    <Button
                      onClick={() => deleteComment(comment.id)}
                      style={{
                        border: "none",
                        background: "none",
                        color: "red",
                        fontSize: "1.5rem",
                        fontWeight: "bold",
                        lineHeight: "1",
                        padding: "0 0.5rem",
                      }}
                    >
                      -
                    </Button>
                  </div>
                ))}
              </div>
            </FormGroup>
          </Col>

        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={() => setModalOpen(false)}>
          Cancel
        </Button>
        <Button color="danger" onClick={onSubmitTicket} disabled={loading}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
}
